import { Injectable } from '@angular/core';

import type { Observable } from 'rxjs';

import type { Position } from '@core/http-types';
import { injectReportsHttpApiService } from '@core/reports-contracts';
import type {
  ReportDashboard,
  ReportDashboardApiEntity,
  ReportDashboardComponentGridPosition,
  ReportsDashboardCreate,
  ReportsDashboardsHttpClientService,
} from '@core/reports-types';

/**
 * Abstract base class for reports dashboards HTTP client.
 *
 * @deprecated This abstract class exists for backward compatibility with domain-specific
 * implementations (e.g., RecruiteeReportsDashboardsHttpClient). Once the migration to
 * tellent-analytics is complete for all services, this can be removed and domains can
 * use ReportsDashboardsHttpClient directly via dependency injection.
 */
export abstract class ReportsDashboardsHttpClientBase
  implements ReportsDashboardsHttpClientService
{
  private readonly http = injectReportsHttpApiService();

  public fetchCollection(): Observable<{ dashboards: ReportDashboardApiEntity[] }> {
    return this.http.get<{ dashboards: ReportDashboardApiEntity[] }>('/dashboards');
  }

  public fetchOne(id: number): Observable<{ dashboard: ReportDashboardApiEntity }> {
    return this.http.get<{ dashboard: ReportDashboardApiEntity }>(`/dashboards/${id}`);
  }

  public createOne(
    dashboardToCreate: ReportsDashboardCreate,
  ): Observable<{ dashboard: ReportDashboardApiEntity }> {
    return this.http.post<{ dashboard: ReportDashboardApiEntity }>('/dashboards', null, {
      dashboard: dashboardToCreate,
    });
  }

  public updateOne(
    dashboard: Partial<ReportDashboard>,
  ): Observable<{ dashboard: ReportDashboardApiEntity }> {
    return this.http.patch<{ dashboard: ReportDashboardApiEntity }>(
      `/dashboards/${dashboard.id}`,
      null,
      { dashboard },
    );
  }

  public updatePositions(
    changes: Position[],
  ): Observable<{ dashboards: ReportDashboardApiEntity[] }> {
    return this.http.patch<{ dashboards: ReportDashboardApiEntity[] }>(
      '/dashboards/update_positions',
      null,
      { dashboards: changes },
    );
  }

  public removeOne(id: string | number): Observable<{ dashboard: { id: number } }> {
    return this.http.delete(`/dashboards/${id}`);
  }

  public moveComponent(
    componentId: number,
    sourceDashboardId: number,
    targetDashboardId: number,
    grid: ReportDashboardComponentGridPosition,
  ): Observable<void> {
    return this.http.patch(`/dashboards/${sourceDashboardId}/move`, null, {
      component: { id: componentId, dashboardId: targetDashboardId, grid },
    });
  }
}

@Injectable()
export class ReportsDashboardsHttpClient
  extends ReportsDashboardsHttpClientBase
  implements ReportsDashboardsHttpClientService {}
